import { Table } from 'antd';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './useResizableTableColumns.css';

const MIN_COLUMN_WIDTH = 72;
const STORAGE_PREFIX = 'twa.externalMaps.columnWidths.';

export const externalMapsColumnStorageKey = (entityType: number): string =>
    STORAGE_PREFIX + String(entityType);

const readStoredWidths = (storageKey: string): Record<string, number> => {
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as Record<string, number>;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeStoredWidths = (storageKey: string, widths: Record<string, number>): void => {
    try {
        window.localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
        // quota / private mode
    }
};

const columnKey = (col: ColumnType<unknown>, index: number): string =>
    String(col.key ?? col.dataIndex ?? index);

const defaultWidthFor = (col: ColumnType<unknown>, index: number): number => {
    if (typeof col.width === 'number') {
        return col.width;
    }
    const key = columnKey(col, index);
    switch (key) {
        case 'hungary':
            return 280;
        case 'sync':
            return 130;
        case 'arena':
            return 220;
        case 'suggestion':
            return 320;
        case 'map':
            return 260;
        case 'create':
            return 110;
        default:
            return 160;
    }
};

type ResizableHeaderCellProps = React.HTMLAttributes<HTMLTableCellElement> & {
    width?: number;
    onResize?: (width: number) => void;
};

const ResizableHeaderCell: React.FC<ResizableHeaderCellProps> = ({
    width,
    onResize,
    children,
    ...rest
}) => {
    if (!width || !onResize) {
        return <th {...rest}>{children}</th>;
    }

    const onMouseDown = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startWidth = width;
        const onMove = (moveEvent: MouseEvent): void => {
            const next = Math.max(MIN_COLUMN_WIDTH, startWidth + moveEvent.clientX - startX);
            onResize(next);
        };
        const onUp = (): void => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    return (
        <th {...rest} style={{ ...rest.style, position: 'relative', width }}>
            {children}
            <span
                className="twa-col-resize-handle"
                role="separator"
                aria-orientation="vertical"
                onMouseDown={onMouseDown}
            />
        </th>
    );
};

// T is used in Table<T>; eslint does not count type-parameter references inside typeof.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ResizableTableProps<T extends object> = React.ComponentProps<typeof Table<T>> & {
    storageKey: string;
    onRegisterReset?: (resetColumnWidths: () => void) => void;
};

function ResizableTableInner<T extends object>({
    storageKey,
    columns = [],
    onRegisterReset,
    ...tableProps
}: ResizableTableProps<T>): React.ReactElement {
    const [widths, setWidths] = useState<Record<string, number>>(() =>
        readStoredWidths(storageKey)
    );

    useEffect(() => {
        setWidths(readStoredWidths(storageKey));
    }, [storageKey]);

    const setColumnWidth = useCallback(
        (key: string, nextWidth: number): void => {
            setWidths((prev) => {
                const next = { ...prev, [key]: nextWidth };
                writeStoredWidths(storageKey, next);
                return next;
            });
        },
        [storageKey]
    );

    const resetColumnWidths = useCallback((): void => {
        try {
            window.localStorage.removeItem(storageKey);
        } catch {
            // ignore
        }
        setWidths({});
    }, [storageKey]);

    useEffect(() => {
        onRegisterReset?.(resetColumnWidths);
    }, [onRegisterReset, resetColumnWidths]);

    const mergedColumns = useMemo((): ColumnsType<T> => {
        return columns.map((col, index) => {
            const key = columnKey(col as ColumnType<unknown>, index);
            const width = widths[key] ?? defaultWidthFor(col as ColumnType<unknown>, index);
            const onResize = (nextWidth: number): void => {
                setColumnWidth(key, nextWidth);
            };
            return {
                ...col,
                width,
                ellipsis: col.ellipsis ?? { showTitle: true },
                onHeaderCell: (): ResizableHeaderCellProps => ({
                    width,
                    onResize,
                }),
            };
        });
    }, [columns, setColumnWidth, widths]);

    const components = useMemo(
        () => ({
            header: {
                cell: ResizableHeaderCell,
            },
        }),
        []
    );

    return (
        <Table<T>
            {...tableProps}
            columns={mergedColumns}
            components={components}
            tableLayout="fixed"
        />
    );
}

export function ResizableTable<T extends object>(props: ResizableTableProps<T>): React.ReactElement {
    return ResizableTableInner(props);
}
